import type {
    AgenteMonedaView,
    CoberturaDTO,
    CotizacionAseguradoDTO,
    CotizacionItemDTO,
    CotizacionStatus,
    CotizacionValorDTO,
    PagaebleResult,
    SearchAseguradoraDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatActivityDate, formatCompactDate, formatSpokenDate } from '../../../utils/dates'
import type { DateInput } from '../../../utils/dates'
import { formatAmount, parseAmount } from '../../../utils/numbers'
import { labelize, money, statusKey } from '../../customers/common/customer-common'
import { FORMA_PAGO } from '../../policies/common/policy-common'

export {
    errorMessage,
    formatCount,
    formatSpokenDate,
    initials,
    labelize,
    money,
    pageWindow,
    personName,
    RamoMark,
    statusKey,
} from '../../customers/common/customer-common'
export { FORMA_PAGO, isBusy, RAMOS, selectMenu } from '../../policies/common/policy-common'
export type { FormaPagoOption, LoadState } from '../../policies/common/policy-common'
export { formatActivityDate, formatCompactDate }

export const QUOTES_PATH = '/cotizaciones'
export const NEW_QUOTE_PATH = `${QUOTES_PATH}/nueva`
export const QUOTE_TEMPLATES_PATH = `${QUOTES_PATH}/templates`
/** Visor público; el enlace que recibe el cliente agrega `?uuid=` para marcar la cotización como vista. */
export const QUOTE_VIEWER_PATH = `${QUOTES_PATH}/visor`
export const NEW_QUOTE_TEMPLATE_PATH = `${QUOTE_TEMPLATES_PATH}/nuevo`

export interface QuoteLocationState {
    backUrl?: string
    detailId?: number
}

export const editQuoteLocation = (id: number, backUrl = QUOTES_PATH): { pathname: string; state: QuoteLocationState } => ({
    pathname: `${QUOTES_PATH}/${id}/edit`,
    state: { backUrl },
})

export const editQuoteTemplateLocation = (id: number, backUrl = QUOTE_TEMPLATES_PATH): { pathname: string; state: QuoteLocationState } => ({
    pathname: `${QUOTE_TEMPLATES_PATH}/${id}/edit`,
    state: { backUrl },
})

/** `eliminada` no se ofrece: se llega a ella al borrar la cotización. */
export const COTIZACION_STATUS: Array<{ id: CotizacionStatus; label: string }> = [
    { id: 'captura', label: 'Captura' },
    { id: 'enviada', label: 'Enviada' },
    { id: 'revision', label: 'En revisión' },
    { id: 'ganada', label: 'Ganada' },
    { id: 'perdida', label: 'Perdida' },
    { id: 'cancelada', label: 'Cancelada' },
]

const CLOSED_STATUS: CotizacionStatus[] = ['ganada', 'perdida', 'cancelada', 'eliminada']

export const isClosedQuote = (status?: string | null) => CLOSED_STATUS.includes(statusKey(status) as CotizacionStatus)

export interface QuoteStatusChip {
    label: string
    className: string
}

const statusClass = (status?: string | null) => {
    const value = statusKey(status)
    if (value === 'ganada') return 'bg-k-ok-soft text-k-ok'
    if (value === 'perdida' || value === 'cancelada' || value === 'eliminada') return 'bg-k-danger-soft text-k-danger-ink'
    if (value === 'enviada') return 'bg-tone-blue text-tone-blue-ink'
    if (value === 'revision') return 'bg-tone-purple text-tone-purple-ink'
    return 'bg-k-line text-k-ink-2'
}

export const quoteStatusView = (status?: string | null): QuoteStatusChip => ({
    label: COTIZACION_STATUS.find((item) => item.id === statusKey(status))?.label || labelize(status) || '—',
    className: statusClass(status),
})

export interface DueView {
    label: string
    note: string
    noteClass: string
}

const daysText = (days: number) => `${days} ${days === 1 ? 'día' : 'días'}`

/** Vigencia de la cotización con aviso mientras siga abierta. */
export const dueView = (fecha?: DateInput, status?: string | null): DueView => {
    const label = formatCompactDate(fecha) || '—'
    const days = daysFromToday(fecha)
    if (days == null || isClosedQuote(status)) return { label, note: '', noteClass: 'text-k-muted' }
    if (days < 0) return { label, note: `Venció hace ${daysText(Math.abs(days))}`, noteClass: 'text-k-danger' }
    if (days === 0) return { label, note: 'Vence hoy', noteClass: 'text-tone-orange-ink' }
    return { label, note: `Vence en ${daysText(days)}`, noteClass: days <= 3 ? 'text-tone-orange-ink' : 'text-k-muted' }
}

export interface TrackingView {
    label: string
    detail: string
    className: string
}

/** Seguimiento del enlace enviado al cliente: vista > enviada > sin enviar. */
export const trackingView = (fechaVista?: string | null, fechaEnvio?: string | null): TrackingView => {
    if (fechaVista) return { label: 'Vista por el cliente', detail: formatActivityDate(fechaVista) || '', className: 'text-k-ok' }
    if (fechaEnvio) return { label: 'Enviada', detail: formatActivityDate(fechaEnvio) || '', className: 'text-tone-blue-ink' }
    return { label: 'Sin enviar', detail: '', className: 'text-k-muted' }
}

/** Campos del plan que aplican según el ramo. */
export const ramoFields = (ramo?: string | null) => {
    const value = statusKey(ramo)
    return {
        deducible: value !== 'vida',
        salud: value === 'salud',
        caracteristicas: true,
        asegurados: value === 'vida' || value === 'salud',
        vehiculo: value === 'auto',
        bien: value === 'diversos',
    }
}

export type PlanValueField = 'sumaAsegurada' | 'deducible' | 'coaseguro' | 'topeCoaseguro' | 'copago'

/** Datos del plan que se capturan como monto (`###,##0.00`). */
export const PLAN_AMOUNT_FIELDS: PlanValueField[] = ['sumaAsegurada', 'deducible', 'coaseguro']

export const isPlanAmountField = (field: PlanValueField) => PLAN_AMOUNT_FIELDS.includes(field)

const PLAN_VALUE_LABELS: Array<{ key: PlanValueField; label: string; applies: (fields: ReturnType<typeof ramoFields>) => boolean }> = [
    { key: 'sumaAsegurada', label: 'Suma asegurada', applies: () => true },
    { key: 'deducible', label: 'Deducible', applies: (fields) => fields.deducible },
    { key: 'coaseguro', label: 'Coaseguro', applies: (fields) => fields.salud },
    { key: 'topeCoaseguro', label: 'Tope de coaseguro', applies: (fields) => fields.salud },
    { key: 'copago', label: 'Copago', applies: (fields) => fields.salud },
]

/** Datos del plan que aplican al ramo, en orden de captura; `sumaAsegurada` es el único obligatorio. */
export const planValueFields = (ramo?: string | null) => {
    const fields = ramoFields(ramo)
    return PLAN_VALUE_LABELS
        .filter((item) => item.applies(fields))
        .map((item) => ({ key: item.key, label: item.label, required: item.key === 'sumaAsegurada', amount: isPlanAmountField(item.key) }))
}

const NUMERIC_TEXT = /^\d[\d,]*(\.\d*)?$|^\.\d+$/

/** `1500000` → `1,500,000.00`; el texto guardado que no es número ("10%", "Amparada") se deja igual. */
export const formatPlanAmount = (value?: string | null) => {
    const text = String(value || '').trim()
    return NUMERIC_TEXT.test(text) ? formatAmount(parseAmount(text)) : text
}

export interface QuoteDetailValue {
    key: string
    descripcion: string
    valor: string
}

export interface QuotePaymentPlan {
    key: string
    label: string
    total: string
    detail: string
    contado: boolean
    primerRecibo: string
    subsecuente: string
    /** Recibos después del primero. */
    subsecuentes: number
}

export interface QuoteProposal {
    id: number
    title: string
    aseguradora: string
    logo: string | null
    plan: string
    moneda: string
    campos: Array<{ label: string; value: string }>
    caracteristicas: QuoteDetailValue[]
    coberturas: QuoteDetailValue[]
    planes: QuotePaymentPlan[]
}

export const sortedValues = (items?: CotizacionValorDTO[] | null): QuoteDetailValue[] =>
    [...(items || [])]
        .sort((left, right) => Number(left.orden ?? 0) - Number(right.orden ?? 0))
        .map((item, index) => ({ key: String(item.id ?? index), descripcion: item.descripcion || '—', valor: item.valor || '—' }))

export const toProposal = (item: CotizacionItemDTO, index: number, ramo?: string | null): QuoteProposal => {
    const moneda = item.moneda || 'MXN'
    return {
        id: item.id,
        title: `Propuesta ${index + 1}`,
        aseguradora: aseguradoraLabel(item.aseguradora) || '—',
        logo: item.aseguradora?.logoM || null,
        plan: item.plan || '—',
        moneda,
        campos: planValueFields(ramo).map((field) => ({ label: field.label, value: item[field.key] || '' })),
        caracteristicas: sortedValues(item.caracteristicas),
        coberturas: sortedValues(item.coberturas),
        planes: (item.planesPago || []).map((plan, planIndex) => {
            const forma = FORMA_PAGO.find((option) => option.key === plan.formaPago)
            const contado = plan.formaPago === 'contado'
            return {
                key: String(plan.id ?? planIndex),
                label: forma?.label || labelize(plan.formaPago) || '—',
                total: money(plan.total, moneda),
                detail: contado
                    ? 'Pago único'
                    : `${money(plan.primaPrimerRecibo, moneda)} primer recibo · ${money(plan.primaReciboSub, moneda)} subsecuentes`,
                contado,
                primerRecibo: money(plan.primaPrimerRecibo, moneda),
                subsecuente: money(plan.primaReciboSub, moneda),
                subsecuentes: Math.max((forma?.parcialidades || 1) - 1, 0),
            }
        }),
    }
}

export const quoteInsured = (item: CotizacionAseguradoDTO, index: number) => ({
    key: String(item.id ?? index),
    nombre: item.nombre || '—',
    parentesco: labelize(item.parentesco),
    genero: labelize(item.genero),
    nacimiento: formatSpokenDate(item.fecNacimiento),
    edad: item.edad,
})

export const PLAN_MAX = 100
export const PLAN_VALUE_MAX = 50
export const DESCRIPCION_MAX = 150
export const CARACTERISTICA_VALOR_MAX = 150
export const COBERTURA_VALOR_MAX = 50

export const aseguradoraLabel = (aseguradora?: Pick<SearchAseguradoraDTO, 'aseguradora' | 'abreviacion'> | null) => {
    if (!aseguradora) return ''
    const name = aseguradora.aseguradora || aseguradora.abreviacion || ''
    return aseguradora.abreviacion && aseguradora.abreviacion !== name ? `${name} (${aseguradora.abreviacion})` : name
}

/** Opción del selector de aseguradora; se arma igual desde el catálogo o desde una cotización guardada. */
export interface QuoteInsurerOption {
    id: number
    aseguradora: string | null
    abreviacion: string | null
}

export const insurerOption = (aseguradora?: { id: number | null; aseguradora: string | null; abreviacion: string | null } | null): QuoteInsurerOption | null =>
    aseguradora?.id ? { id: aseguradora.id, aseguradora: aseguradora.aseguradora, abreviacion: aseguradora.abreviacion } : null

export interface QuoteCatalogs {
    aseguradoras: SearchAseguradoraDTO[]
    monedas: AgenteMonedaView[]
}

/** Aseguradoras activas y monedas del agente para los formularios de cotización y template. */
export const fetchQuoteCatalogs = async (): Promise<QuoteCatalogs> => {
    const [aseguradorasPayload, monedasPayload]: [
        WrapperResponse<PagaebleResult<SearchAseguradoraDTO>> | null,
        WrapperResponse<AgenteMonedaView[]> | null,
    ] = await Promise.all([
        APIInvoke.invokeGET('/aseguradoras?size=99999&status=activo&order=aseguradora%20asc').then((response: Response) => response.json()),
        APIInvoke.invokeGET('/monedas').then((response: Response) => response.json()),
    ])
    if (!aseguradorasPayload?.ok) throw new Error(aseguradorasPayload?.message || 'No se pudieron cargar las aseguradoras.')
    if (!monedasPayload?.ok) throw new Error(monedasPayload?.message || 'No se pudieron cargar las monedas.')
    return {
        aseguradoras: aseguradorasPayload.body?.items || [],
        monedas: monedasPayload.body || [],
    }
}

/** Descripciones del catálogo de coberturas del ramo, para sugerir al capturar. */
export const fetchCoberturaSuggestions = async (ramo: string): Promise<string[]> => {
    const response = await APIInvoke.invokeGET(`/coberturas?ramo=${encodeURIComponent(ramo)}&size=500`)
    const payload: WrapperResponse<PagaebleResult<CoberturaDTO>> | null = await response.json()
    if (!payload?.ok) return []
    return Array.from(new Set((payload.body?.items || []).map((item) => item.descripcion || '').filter(Boolean)))
}
